import { z } from 'zod';

/**
 * Reglas de contraseña visibles para el usuario.
 *
 * El mínimo de 12 caracteres es requisito del encargo. La validación definitiva
 * (historial de las últimas 5, Argon2id) ocurre en el backend; aquí sólo se
 * anticipa el rechazo para no hacerle perder el viaje al usuario.
 */
export const PASSWORD_MIN_LENGTH = 12;

export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `Al menos ${PASSWORD_MIN_LENGTH} caracteres`,
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  { id: 'upper', label: 'Una letra mayúscula', test: (value) => /[A-ZÁÉÍÓÚÑ]/.test(value) },
  { id: 'lower', label: 'Una letra minúscula', test: (value) => /[a-záéíóúñ]/.test(value) },
  { id: 'digit', label: 'Un número', test: (value) => /\d/.test(value) },
  {
    id: 'symbol',
    label: 'Un carácter especial',
    test: (value) => /[^\p{L}\p{N}]/u.test(value),
  },
];

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres.`)
  .refine((value) => PASSWORD_RULES.every((rule) => rule.test(value)), {
    message: 'La contraseña no cumple todas las reglas indicadas.',
  });

export interface PasswordStrength {
  /** 0 = vacía, 1 = débil, 4 = fuerte. */
  level: 0 | 1 | 2 | 3 | 4;
  label: string;
  satisfied: string[];
}

export function evaluatePassword(value: string): PasswordStrength {
  if (value.length === 0) return { level: 0, label: 'Sin contraseña', satisfied: [] };

  const satisfied = PASSWORD_RULES.filter((rule) => rule.test(value)).map((rule) => rule.id);
  let score = satisfied.length;
  if (value.length >= 16) score += 1;

  const level: PasswordStrength['level'] =
    score <= 2 ? 1 : score === 3 ? 2 : score <= 5 ? 3 : 4;

  const label =
    level === 1 ? 'Débil' : level === 2 ? 'Regular' : level === 3 ? 'Aceptable' : 'Fuerte';

  return { level, label, satisfied };
}
